import { type FC, useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Cookies from 'js-cookie';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide';
// MUI
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Paper,
  Button,
  ButtonGroup,
} from '@mui/material';

// common
import Icon from 'common/components/Icon';
import { AUTHENTICATION_TOKEN } from 'common/constants';

// Setup PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string | null;
  title?: string;
}

const PdfViewer: FC<PdfViewerProps> = ({ open, onClose, pdfUrl, title = 'PDF Viewer' }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   if (open && pdfUrl) {
  //     setLoading(true);
  //     setNumPages(null);
  //     setCurrentPage(1);
  //     setScale(1.0);

  //     // Test if PDF URL is accessible
  //     fetch(pdfUrl, { method: "HEAD" })
  //       .then((response) => {
  //         console.log("PDF URL accessible:", response.ok, response.status);
  //       })
  //       .catch((error) => {
  //         console.error("PDF URL not accessible:", error);
  //       });

  //     // Add timeout to prevent infinite loading
  //     const timeout = setTimeout(() => {
  //       setLoading(false);
  //     }, 60000); // 60 seconds timeout

  //     return () => clearTimeout(timeout);
  //   }
  // }, [open, pdfUrl]);

  const handleClose = useCallback(() => {
    setLoading(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!open || !numPages) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setCurrentPage((prev) => Math.max(1, prev - 1));
          break;
        case 'ArrowRight':
          event.preventDefault();
          setCurrentPage((prev) => Math.min(numPages, prev + 1));
          break;
        case 'Escape':
          handleClose();
          break;
        case '+':
        case '=':
          event.preventDefault();
          setScale((prev) => Math.min(2.0, prev + 0.25));
          break;
        case '-':
          event.preventDefault();
          setScale((prev) => Math.max(0.5, prev - 0.25));
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [open, numPages, currentPage, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} sx={{ minHeight: 300, minWidth: 200 }}>
      <DialogTitle
        sx={{
          bgcolor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
          padding: { xs: '12px 16px', md: '16px 24px' },
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flex: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {title}
          </Typography>
          {numPages && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {currentPage}/{numPages}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Zoom Controls */}
          <ButtonGroup
            size="small"
            variant="outlined"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <Button
              aria-label="Thu nhỏ"
              onClick={() => setScale(Math.max(0.5, scale - 0.25))}
              disabled={scale <= 0.5}
            >
              <Icon icon={ZoomOut} size={16} />
            </Button>
            <Button
              aria-label="Phóng to"
              onClick={() => setScale(Math.min(2.0, scale + 0.25))}
              disabled={scale >= 2.0}
            >
              <Icon icon={ZoomIn} size={16} />
            </Button>
          </ButtonGroup>

          {/* Navigation Controls */}
          {numPages && numPages > 1 && (
            <ButtonGroup size="small" variant="outlined">
              <Button
                aria-label="Trang trước"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                sx={{ minWidth: { xs: '36px', sm: '40px' } }}
              >
                <Icon icon={ChevronLeft} size={16} />
              </Button>
              <Button
                aria-label="Trang sau"
                onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                disabled={currentPage >= numPages}
                sx={{ minWidth: { xs: '36px', sm: '40px' } }}
              >
                <Icon icon={ChevronRight} size={16} />
              </Button>
            </ButtonGroup>
          )}
        </Box>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            ml: { xs: 0.5, sm: 1 },
          }}
        >
          <Icon icon={X} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          overflow: 'auto',
          minWidth: '300px',
          minHeight: '400px',
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {pdfUrl && !loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: 2,
              minHeight: '100%',
              width: '100%',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 1, sm: 2 },
                backgroundColor: 'white',
                borderRadius: { xs: 0, sm: 1 },
                boxShadow: { xs: 'none', sm: '0 4px 20px rgba(0,0,0,0.1)' },
              }}
            >
              <Document
                file={{
                  url: pdfUrl,
                  withCredentials: true,
                  httpHeaders: Cookies.get(AUTHENTICATION_TOKEN)
                    ? { 'hvs-token': Cookies.get(AUTHENTICATION_TOKEN) as string }
                    : undefined,
                }}
                options={{ disableRange: true, disableStream: true }}
                onLoadSuccess={({ numPages }) => {
                  setNumPages(numPages);
                  setLoading(false);
                }}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  setLoading(false);
                }}
                loading={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
                error={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 4,
                    }}
                  >
                    <Typography color="error" gutterBottom>
                      Failed to load PDF
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The PDF file could not be loaded. Please try again.
                    </Typography>
                  </Box>
                }
              >
                <Page
                  pageNumber={currentPage}
                  width={Math.min(window.innerWidth - 32, 595) * scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  }
                />
              </Document>
            </Paper>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewer;
